import { z } from 'zod'
import { emailSchema } from './common'

/**
 * The volunteer interest list: people who want to help but haven't picked a shift. Areas are a
 * setting (`volunteers.interest_areas`) so the org can change them; availability is fixed.
 */
export const DEFAULT_INTEREST_AREAS = [
  'Festival day (info booth, wristbands, stage support)',
  'Setup and teardown',
  'Parade (marshals, lineup)',
  'Youth programs',
  'Outreach and tabling (farmers markets, community events)',
  'Fundraising and sponsorship',
  'Photography and video',
  'Social media and design',
  'Office and admin',
  'Accessibility support (ASL, sensory, mobility)',
  'Hospitality (performers, green room)',
  'Anything you need',
]

export const INTEREST_AVAILABILITY = [
  'Weekday daytime',
  'Weekday evenings',
  'Weekends',
  'Festival day',
  'Occasional/one-off',
] as const

export const CONTACT_PREFERENCES = ['email', 'text', 'either'] as const
export const CONTACT_PREFERENCE_LABELS: Record<(typeof CONTACT_PREFERENCES)[number], string> = {
  email: 'Email',
  text: 'Text message',
  either: 'Either is fine',
}

const blankToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v)
const PHONE_RE = /^[+()\d\s.-]{7,25}$/

/**
 * The public "join our volunteer list" form. `homepage` is a honeypot people never see.
 * `marketingOptIn` is the shared "send me news" checkbox; the route adds the person to the email
 * list when it is ticked.
 */
export const volunteerInterestInputSchema = z
  .object({
    name: z.string().trim().min(2, 'Tell us your name').max(120),
    email: emailSchema,
    phone: z.preprocess(
      blankToNull,
      z.string().trim().regex(PHONE_RE, 'Enter a valid phone number').nullable().optional(),
    ),
    areas: z.array(z.string().trim().min(1).max(160)).max(40).default([]),
    availability: z
      .array(z.enum(INTEREST_AVAILABILITY))
      .max(INTEREST_AVAILABILITY.length)
      .default([]),
    skills: z.preprocess(blankToNull, z.string().trim().max(2000).nullable().optional()),
    notes: z.preprocess(blankToNull, z.string().trim().max(2000).nullable().optional()),
    contactPreference: z.enum(CONTACT_PREFERENCES).default('email'),
    marketingOptIn: z.boolean().optional(),
    homepage: z.string().max(200).optional(),
  })
  .refine((v) => v.contactPreference === 'email' || !!v.phone, {
    message: 'Add a phone number so we can text you',
    path: ['phone'],
  })
export type VolunteerInterestInput = z.infer<typeof volunteerInterestInputSchema>

export const INTEREST_STATUSES = ['active', 'archived'] as const

export const interestQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  area: z.string().trim().max(160).optional(),
  availability: z.enum(INTEREST_AVAILABILITY).optional(),
  status: z.enum([...INTEREST_STATUSES, 'all']).default('active'),
})
export type InterestQuery = z.infer<typeof interestQuerySchema>

export const interestStatusSchema = z.object({ status: z.enum(INTEREST_STATUSES) })

/** "Email these people": a one-off message to everyone the filters match. Body is plain text. */
export const interestEmailSchema = z.object({
  filters: interestQuerySchema.omit({ status: true }).default({}),
  subject: z.string().trim().min(3).max(160),
  body: z.string().trim().min(10).max(10_000),
})
export type InterestEmailInput = z.infer<typeof interestEmailSchema>

/** "Notify interested volunteers" about one role: people whose areas match any picked area. */
export const notifyInterestedSchema = z.object({
  areas: z.array(z.string().trim().min(1).max(160)).min(1).max(40),
  message: z.preprocess(blankToNull, z.string().trim().max(5000).nullable().optional()),
})

export interface VolunteerInterestView {
  id: string
  personId: string | null
  name: string
  email: string
  phone: string | null
  areas: string[]
  availability: string[]
  skills: string | null
  notes: string | null
  contactPreference: (typeof CONTACT_PREFERENCES)[number]
  status: (typeof INTEREST_STATUSES)[number]
  source: string | null
  doNotContact: boolean
  createdAt: string
  updatedAt: string
}

export interface InterestListResult {
  items: VolunteerInterestView[]
  total: number
  /** Active people per area (whole list, not just the filtered rows). */
  areaCounts: { area: string; count: number }[]
  areas: string[]
}

/** What a signed-in visitor already told us, to prefill the form. */
export interface MyVolunteerInterest {
  name: string
  email: string
  phone: string | null
  areas: string[]
  availability: string[]
  skills: string | null
  notes: string | null
  contactPreference: (typeof CONTACT_PREFERENCES)[number]
  onList: boolean
}

export interface InterestEmailResult {
  recipients: number
  queued: number
  /** People who asked not to be contacted: recorded, never delivered. */
  doNotContact: number
}
